import { CodedStatusMessage } from '@gfe/core/models/coded-status-message.model';
import { WarningOccurrence } from '@gfe/core/services/gbe-error-parser/warning.model';
import { displayedText } from '@gfe/shared/components/finding-message/finding-display';

import { matchesSearch } from '@shared/utils/matches-search';

/**
 * An occurrence kept next to the message it came from, so the row's copy action can
 * still offer that message's raw payload after occurrences of one code are grouped
 * across several messages.
 */
export interface GroupedOccurrence extends WarningOccurrence {
  raw: CodedStatusMessage;
  /**
   * Per occurrence, not per group: a code's occurrences can come from a blocking finding and from
   * a non-blocking one, and only the first count as blocking, as the plan's status counts them.
   */
  blocksOptimization: boolean;
  /**
   * Unique within its group and kept by the same occurrence across findings updates, so a re-sort
   * moves its row instead of handing that row, and a finding card anchored to it, another one.
   */
  key: string;
}

export type OccurrenceFilter = (occurrence: WarningOccurrence, title: string) => boolean;

export interface FilterableGroup {
  code: string;
  label: string;
  blocksOptimization: boolean;
  occurrences: GroupedOccurrence[];
}

/** What the search and the scope leave; the picked codes play no part. */
export interface NarrowedFindings {
  /** The categories one can still pick, in listing order. */
  available: FilterableGroup[];
  total: number;
  totalBlocking: number;
  /** Passed the search, then rejected by the scope. */
  hiddenByScope: number;
  searchMatchedNothing: boolean;
}

export interface FilteredFindings extends NarrowedFindings {
  /** What the search, the scope and the picked codes leave, in listing order. */
  groups: FilterableGroup[];
  shown: number;
  shownBlocking: number;
}

function searchableText(
  group: FilterableGroup,
  occurrence: GroupedOccurrence,
  sameDay: string | null
): string {
  return [
    group.label,
    group.code,
    occurrence.body,
    displayedText(occurrence.parts, occurrence.body, sameDay),
    ...occurrence.refs.flatMap((ref) => (ref.uid === null ? [] : [ref.uid])),
    ...occurrence.fields.map((field) => field.value)
  ].join('\n');
}

/** Sizeless rows return 0 against each other, so a stable sort keeps them in their order. */
function bySizeDescending(a: GroupedOccurrence, b: GroupedOccurrence): number {
  if (a.size && b.size) return b.size.seconds - a.size.seconds;
  if (a.size) return -1;
  if (b.size) return 1;
  return 0;
}

export function byBlockingThenCount(a: FilterableGroup, b: FilterableGroup): number {
  return (
    Number(b.blocksOptimization) - Number(a.blocksOptimization) ||
    b.occurrences.length - a.occurrences.length
  );
}

const occurrenceCount = (groups: FilterableGroup[]): number =>
  groups.reduce((sum, group) => sum + group.occurrences.length, 0);

const blockingCount = (groups: FilterableGroup[]): number =>
  groups.reduce(
    (sum, group) =>
      sum + group.occurrences.filter((occurrence) => occurrence.blocksOptimization).length,
    0
  );

/**
 * The title and code take part in every occurrence's text, so a match on either keeps
 * the whole group, and a query may combine a word of the title with one of a row.
 */
export function narrowFindings(args: {
  groups: FilterableGroup[];
  query: string;
  scope: OccurrenceFilter | null;
  sameDay: string | null;
}): NarrowedFindings {
  const query = args.query.trim();
  let passedSearch = 0;
  let hiddenByScope = 0;
  const available: FilterableGroup[] = [];

  for (const group of args.groups) {
    const occurrences = group.occurrences.filter((occurrence) => {
      if (
        query !== '' &&
        !matchesSearch(searchableText(group, occurrence, args.sameDay), query)
      ) {
        return false;
      }
      passedSearch++;
      if (args.scope && !args.scope(occurrence, group.label)) {
        hiddenByScope++;
        return false;
      }
      return true;
    });
    if (occurrences.length > 0) {
      available.push({ ...group, occurrences: occurrences.sort(bySizeDescending) });
    }
  }

  const total = occurrenceCount(args.groups);
  return {
    available,
    total,
    totalBlocking: blockingCount(args.groups),
    hiddenByScope,
    searchMatchedNothing: query !== '' && passedSearch === 0 && total > 0
  };
}

/** Picking no code, or only codes the search and the scope hide, keeps every available one. */
export function pickFindings(
  narrowed: NarrowedFindings,
  picked: ReadonlySet<string>
): FilteredFindings {
  const kept = narrowed.available.filter((group) => picked.has(group.code));
  const groups = (kept.length > 0 ? kept : narrowed.available)
    .slice()
    .sort(byBlockingThenCount);
  return {
    ...narrowed,
    groups,
    shown: occurrenceCount(groups),
    shownBlocking: blockingCount(groups)
  };
}
